import {
  Avatar,
  Box,
  CardActions,
  CardContent,
  CardHeader,
  Checkbox,
  Dialog,
  Grid,
  IconButton,
  LinearProgress,
  Paper,
  TextField,
  Tooltip,
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import SearchIcon from "@mui/icons-material/Search";
import { DataGrid } from "@mui/x-data-grid";
import { frFR } from "@mui/x-data-grid/locales";
import { RefreshOutlined } from "@mui/icons-material";
import { AddOutlined } from "@material-ui/icons";
import useToast from "../../../../hooks/useToast";
import { TYPE_NOTIFICATION } from "../../shares/NotificationBull";
import AppButton from "../../shares/AppButton";
import { getBackendProfilePictureLink } from "../../../../Helpers/utilsHelper";
import { userServices } from "../../../../service/UserService";
import { MembreParLotService } from "../../../../service/MembreParLotService";
import { useQuery } from "../../../../hooks/useQuery";
import MembreForm from "../../mes_association/MembreForm";

const AssignerMembreAssociationParLot = ({
  open,
  selectedLotID,
  onFormClose,
}) => {
  const [loading, setLoading] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [filteredRows, setFilteredRows] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [initialSelectedIds, setInitialSelectedIds] = useState([]);
  const [currentIdMembre, setCurrentIdMembre] = useState(null);
  const [membreFormOpen, setMembreFormOpen] = useState(false);
  const [currentAssociation, setCurrentAssociation] = useState(null);
  const [filtre, setFiltre] = useState({
    mot_cle: "",
  });
  const { Toast, showToast } = useToast();
  const query = useQuery();

  const handleFormClose = () => {
    onFormClose();
    setSelectedIds([]);
    setInitialSelectedIds([]);
  };

  const handleShowInfo = () => {
    userServices.getMyAssociation().then((res) => {
      if (res && res.data) {
        setCurrentAssociation(res.data);
        handleFetchData(res.data);
      }
    });
  };

  const handleOpenMembreForm = (membreId) => {
    setCurrentIdMembre(membreId);
    setMembreFormOpen(true);
  };

  const handleEditMembre = (membreId) => {
    handleOpenMembreForm(membreId);
  };

  const handleFilter = useCallback(
    (filtre) => {
      if (!filtre || String(filtre).trim() === "") {
        setFilteredRows(rows);
        return;
      }
      const mots = filtre
        .toLowerCase()
        .split(" ")
        .filter((mot) => mot.trim() !== "");
      setFilteredRows(
        rows.filter((item) =>
          mots.every((mot) =>
            Object.values(item).some(
              (val) => val && val.toString().toLowerCase().includes(mot),
            ),
          ),
        ),
      );
    },
    [rows],
  );

  const handleFetchData = () => {
    setFormLoading(true);
    MembreParLotService.liste_tous_par_lot({ id_type: query.get("job_id") })
      .then((res) => {
        if (res && res.data) {
          setRows(res.data);
          const initiallySelected = res.data
            //on recupere les membres inscrits (statut 1 : inscrit au Lot courant, statut -1: Ocupé par d'autre lot, statut 0: disponible)
            .filter((r) => r.statut === "1")
            .map((r) => r.id_ext_user);
          setSelectedIds(initiallySelected);
          setInitialSelectedIds(initiallySelected);
        }
      })
      .finally(() => {
        setFormLoading(false);
      });
  };

  const handleCheckboxChange = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((sid) => sid !== id) : [...prev, id],
    );
    setRows((prevRows) =>
      prevRows.map((row) =>
        row.id_sqc_question === id ? { ...row, selected: !row.selected } : row,
      ),
    );
  };

  const getInsertableRows = () => {
    return {
      id_ext_users: selectedIds,
      id_type: query.get("job_id"),
    };
  };

  const handleSave = () => {
    setLoading(true);
    MembreParLotService.insert(getInsertableRows())
      .then((res) => {
        if (res.success) {
          showToast(TYPE_NOTIFICATION.SUCCESS, res.message);
          handleFormClose();
        } else {
          showToast(TYPE_NOTIFICATION.ERROR, res.message);
        }
      })
      .finally(() => {
        setLoading(false);
      });
    setInitialSelectedIds(selectedIds); // Met à jour l'état initial
  };

  const handleCloseMembreForm = () => {
    setMembreFormOpen(false);
    handleFetchData();
  };

  const table_colonne = [
    {
      field: "select",
      headerName: "",
      width: 50,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      resizable: false,
      renderCell: (params) => {
        const isSelected = selectedIds.includes(params.row.id_ext_user);
        const disabled = params.row.statut === "-1";
        return (
          <Tooltip
            title={
              disabled
                ? "Ce membre est deja assigné à un autre lot"
                : isSelected
                  ? "Selectionner pour desassigner"
                  : "Selectionner pour assigner"
            }
          >
            <span>
              <Checkbox
                disabled={disabled}
                checked={isSelected}
                onChange={() => handleCheckboxChange(params.row.id_ext_user)}
                inputProps={{ "aria-label": "controlled" }}
              />
            </span>
          </Tooltip>
        );
      },
    },
    {
      field: "membre_pdp",
      headerName: "",
      minWidth: 80,
      flex: 0.25,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => {
        return (
          <Grid>
            <Avatar
              sx={{
                m: 1,
                width: "clamp(20px, 15vw, 40px)",
                height: "clamp(20px, 15vw, 40px)",
                fontSize: "clamp(10px, 8vw, 18px)",
              }}
              alt={String(params.row.nom).toUpperCase() || "Asso"}
              src={getBackendProfilePictureLink(params.row.photo)}
            />
          </Grid>
        );
      },
    },
    {
      field: "nom",
      headerName: "Nom",
      flex: 1,
      minWidth: 150,
    },
    {
      field: "prenoms",
      headerName: "Prenoms",
      flex: 1,
      minWidth: 150,
    },
    {
      field: "role_responsable",
      headerName: "Fonction",
      flex: 1,
      minWidth: 150,
    },
    {
      field: "telephone1",
      headerName: "Telephone ",
      flex: 1,
      minWidth: 120,
    },
  ];

  useEffect(() => {
    handleFilter(filtre.mot_cle);
  }, [filtre, handleFilter, rows]);

  useEffect(() => {
    if (open) {
      handleShowInfo();
      // handleFetchData(currentAssociation);
    }
  }, [open]);

  return (
    <>
      <MembreForm
        idMembre={currentIdMembre}
        open={membreFormOpen}
        onFormClose={handleCloseMembreForm}
        association_uuid={currentAssociation?.uuid}
      />
      <Dialog open={open} onClose={handleFormClose} fullWidth maxWidth="md">
        <Toast />
        <Paper sx={{ m: 3 }} elevation={0}>
          <Grid>
            <CardHeader
              title={"Assigner membres"}
              action={
                <Grid container spacing={2} justifyContent="space-around">
                  <Grid item>
                    <TextField
                      id="recherche"
                      fullWidth
                      size="small"
                      onChange={(e) =>
                        setFiltre((prev) => {
                          return { ...prev, mot_cle: e.target.value };
                        })
                      }
                      placeholder="Recherche"
                      InputProps={{
                        endAdornment: (
                          <IconButton>
                            <SearchIcon />
                          </IconButton>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item>
                    <IconButton
                      title="Actualiser"
                      color="primary"
                      onClick={(e) => handleFetchData(currentAssociation)}
                    >
                      <RefreshOutlined />
                    </IconButton>
                  </Grid>
                  <Grid item>
                    <IconButton
                      onClick={(e) => handleOpenMembreForm(null)}
                      color="primary"
                      title="Ajouter nouveau membre"
                    >
                      <AddOutlined />
                    </IconButton>
                  </Grid>
                </Grid>
              }
            />
            <CardContent>
              <Grid container /* display={formLoading ? "none" : ""} */>
                <Box display={formLoading ? "" : "none"} sx={{ width: "100%" }}>
                  <LinearProgress />
                </Box>
                <DataGrid
                  sx={{
                    width: {
                      xs: "75.5vw",
                      sm: "90vw",
                      md: "93.5vw",
                      lg: "79.5vw",
                      height: "60vh",
                    },
                  }}
                  columns={table_colonne}
                  rows={filteredRows}
                  getRowId={(row) => row.id_ext_responsable_association}
                  localeText={
                    frFR.components.MuiDataGrid.defaultProps.localeText
                  }
                />
              </Grid>
            </CardContent>
            <CardActions sx={{ justifyContent: "flex-end" }}>
              <AppButton
                loading={loading}
                disabled={formLoading}
                text={"Enregistrer"}
                variant={"contained"}
                size={"medium"}
                onClick={handleSave}
              />
              <AppButton
                text={"Fermer"}
                size={"medium"}
                variant={"outlined"}
                onClick={handleFormClose}
              />
            </CardActions>
          </Grid>
        </Paper>
      </Dialog>
    </>
  );
};

export default AssignerMembreAssociationParLot;
